// Auth service for Redmine authentication
// Integrates with tenant context to automatically use the correct Redmine instance
// based on subdomain (e.g., tenant.domain.com resolves to tenant's Redmine URL)
// Each user authenticates with their own Redmine credentials

import axios, { AxiosInstance } from 'axios'
import { RedmineClient } from '../../packages/api/src'
import { encryptSessionObjectSync, decryptSessionStringSync } from '../../lib/crypto/session-crypt'
import { getRedmineBaseUrl } from '../../lib/tenant-context'

export interface AuthCredentials {
    username: string
    password: string
}

export interface AuthSession {
    user: RedmineUser
    apiKey?: string
    token: string
    expiresAt: number
    jwtToken?: string // JWT token for backend database operations
    /**
     * Credentials are kept in memory during active session but NOT persisted to localStorage.
     * On page refresh, users with password-based auth will need to re-login.
     * API key authentication remains persistent.
     */
    credentials?: {
        username: string
        password: string
        basicAuth: string
    }
}

export interface RedmineUser {
    id: number
    login: string
    firstname: string
    lastname: string
    mail: string
    admin: boolean
    created_on: string
    last_login_on?: string
    api_key?: string
    status: number
    groups?: Array<{
        id: number
        name: string
    }>
}

const SESSION_KEY = 'oxigen_auth_session'
const TOKEN_EXPIRY_HOURS = 24 // Extended to 24 hours for better UX

export class AuthService {
    private client: AxiosInstance
    private apiProxy: string

    constructor() {
        // Use Next.js API proxy to avoid CORS issues
        this.apiProxy = '/api/redmine'
        this.client = axios.create({
            baseURL: this.apiProxy,
            headers: {
                'Content-Type': 'application/json',
            },
            timeout: 15000,
        })
    }

    /**
     * Authenticate user with Redmine using username/password
     */
    async login(credentials: AuthCredentials): Promise<AuthSession> {
        try {
            // Create Basic Auth header using btoa (browser compatible)
            const basicAuth = btoa(`${credentials.username}:${credentials.password}`)

            console.log('[AuthService] Attempting login for user:', credentials.username)

            // Use the API proxy to avoid CORS issues - disable caching
            const response = await fetch('/api/redmine?path=/users/current.json', {
                method: 'GET',
                headers: {
                    'Authorization': `Basic ${basicAuth}`,
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache'
                },
                cache: 'no-store'
            })

            console.log('[AuthService] Login response status:', response.status)
            console.log('[AuthService] Login response headers:', Object.fromEntries(response.headers.entries()))

            if (!response.ok) {
                if (response.status === 401) {
                    console.error('[AuthService] Login failed: Invalid credentials')
                    throw new Error('Invalid username or password')
                }
                console.error('[AuthService] Login failed:', response.statusText)
                throw new Error(`Authentication failed: ${response.statusText}`)
            }

            const responseData = await response.json()
            const user: RedmineUser = responseData.user

            console.log('[AuthService] Login successful for user:', user.login)

            // Auto-register user with backend to get JWT token
            console.log('[AuthService] About to call registerUserWithBackend...')
            const jwtToken = await this.registerUserWithBackend(user, basicAuth)
            console.log('[AuthService] Returned from registerUserWithBackend, token:', jwtToken ? 'RECEIVED' : 'NONE')

            // Generate a session token
            const token = this.generateSessionToken(user.id)
            const expiresAt = Date.now() + (TOKEN_EXPIRY_HOURS * 60 * 60 * 1000)

            // Keep credentials in memory only (not persisted to localStorage)
            const session: AuthSession = {
                user,
                token,
                expiresAt,
                apiKey: user.api_key,
                jwtToken, // Store JWT token for backend API calls
                // Store credentials in memory only - they won't be persisted
                credentials: {
                    username: credentials.username,
                    password: credentials.password,
                    basicAuth
                }
            }

            // Store session in localStorage (credentials will be stripped by storeSession)
            this.storeSession(session)

            return session
        } catch (error) {
            if (error instanceof Error) {
                throw error
            }
            throw new Error('Authentication failed')
        }
    }

    /**
     * Authenticate using API key
     */
    async loginWithApiKey(apiKey: string): Promise<AuthSession> {
        try {

            // Use the API proxy to avoid CORS issues
            const response = await fetch('/api/redmine?path=/users/current.json', {
                headers: {
                    'X-Redmine-API-Key': apiKey,
                    'Content-Type': 'application/json'
                },
            })

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Invalid API key')
                }
                throw new Error(`Authentication failed: ${response.statusText}`)
            }

            const responseData = await response.json()
            const user: RedmineUser = responseData.user

            // Auto-register user with backend to get JWT token
            const jwtToken = await this.registerUserWithBackend(user, apiKey)

            const token = this.generateSessionToken(user.id)
            const expiresAt = Date.now() + (TOKEN_EXPIRY_HOURS * 60 * 60 * 1000)

            const session: AuthSession = {
                user,
                token,
                expiresAt,
                apiKey,
                jwtToken, // Store JWT token for backend API calls
            }

            this.storeSession(session)

            return session
        } catch (error) {
            if (error instanceof Error) {
                throw error
            }
            throw new Error('API key authentication failed')
        }
    }

    /**
     * Get current session from storage
     */
    getSession(): AuthSession | null {
        try {
            if (typeof window === 'undefined') {
                // Server-side rendering, no session available
                return null
            }

            const sessionData = localStorage.getItem(SESSION_KEY)

            if (!sessionData) return null

            // Try to decrypt; if decrypt fails, fall back to raw parse for backward compatibility
            let session: AuthSession | null = null
            const decrypted = decryptSessionStringSync(sessionData)
            if (decrypted) {
                session = decrypted as AuthSession
            } else {
                try {
                    session = JSON.parse(sessionData) as AuthSession
                } catch (err) {
                    session = null
                }
            }

            // Check if session has expired (add 5 minute buffer to prevent edge cases)
            const currentTime = Date.now()
            const expirationBuffer = 5 * 60 * 1000 // 5 minutes

            if (!session || !session.expiresAt) {
                this.logout()
                return null
            }

            if (currentTime >= (session.expiresAt - expirationBuffer)) {
                this.logout()
                return null
            }

            return session
        } catch (error) {
            this.logout()
            return null
        }
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        const session = this.getSession()
        return session !== null
    }

    /**
     * Logout user and clear session
     */
    logout(): void {
        if (typeof window !== 'undefined') {
            localStorage.removeItem(SESSION_KEY)
            // Clear JWT token cookie
            document.cookie = 'jwt-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
        }
    }

    /**
     * Check if session is valid (not expired)
     */
    private isSessionValid(session: AuthSession): boolean {
        return Date.now() < session.expiresAt
    }

    /**
     * Auto-register user with backend to get JWT token
     * Called after successful Redmine authentication
     */
    private async registerUserWithBackend(user: RedmineUser, apiKey: string): Promise<string | undefined> {
        try {
            // Get tenant context
            const tenantData = this.getTenantContext()
            if (!tenantData || !tenantData.id) {
                console.warn('[AuthService] No tenant context for backend registration')
                return undefined
            }

            if (!tenantData.backendUrl) {
                console.error('[AuthService] Backend URL not found in tenant configuration')
                return undefined
            }

            console.log('[AuthService] Registering user with backend:', user.login)
            console.log('[AuthService] Tenant ID:', tenantData.id)
            console.log('[AuthService] Redmine User ID:', user.id)
            console.log('[AuthService] User object:', JSON.stringify(user, null, 2))

            // Get backend URL from tenant configuration (provided by backend)
            const registerUrl = `${tenantData.backendUrl}/api/public/tenants/auth/register`

            console.log('[AuthService] Backend URL from tenant config:', tenantData.backendUrl)
            console.log('[AuthService] Backend registration URL:', registerUrl)

            const payload = {
                tenantId: tenantData.id,
                redmineUserId: user.id,
                redmineLogin: user.login,
                redmineApiKey: apiKey,
                firstname: user.firstname,
                lastname: user.lastname,
                email: user.mail || user.login, // Use login as email if not provided by Redmine
                isAdmin: user.admin || false,
            }

            console.log('[AuthService] Registration payload:', JSON.stringify(payload, null, 2))

            const response = await fetch(registerUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            })

            console.log('[AuthService] Backend registration response status:', response.status)

            if (!response.ok) {
                const errorText = await response.text()
                console.error('[AuthService] Backend registration failed:', response.status, errorText)
                return undefined
            }

            const data = await response.json()
            console.log('[AuthService] Backend registration successful')
            console.log('[AuthService] JWT token received:', data.accessToken ? `${data.accessToken.substring(0, 20)}...` : 'NONE')
            return data.accessToken
        } catch (error) {
            console.error('[AuthService] Backend registration error:', error)
            return undefined
        }
    }

    /**
     * Ensure the current session has a valid JWT token.
     * If the session exists but has no JWT (e.g., backend was unreachable during login),
     * or if the JWT has been invalidated (e.g., after a backend deployment),
     * this will attempt to re-register with the backend to obtain one.
     * 
     * First tries the lightweight refresh endpoint (no credentials needed),
     * then falls back to full re-registration if refresh fails.
     * 
     * @returns The updated session with JWT, or null if re-registration failed
     */
    async ensureJwtToken(): Promise<AuthSession | null> {
        const session = this.getSession()
        if (!session?.user) return null

        // Already have a JWT token — check if it's still valid
        if (session.jwtToken) {
            // Try to decode and check expiry without verification
            try {
                const parts = session.jwtToken.split('.')
                if (parts.length === 3) {
                    const payload = JSON.parse(atob(parts[1]))
                    const expiresAt = (payload.exp || 0) * 1000
                    const bufferMs = 5 * 60 * 1000 // 5 minute buffer
                    if (Date.now() < expiresAt - bufferMs) {
                        return session // Token is still valid
                    }
                    console.log('[AuthService] JWT token expiring soon or expired, refreshing...')
                }
            } catch {
                // Can't decode — treat as potentially invalid
                console.log('[AuthService] JWT token decode failed, attempting refresh...')
            }
        } else {
            console.log('[AuthService] Session has no JWT token, attempting to obtain one...')
        }

        // Try the lightweight refresh endpoint first (uses tenantId + redmineUserId)
        try {
            const tenantData = this.getTenantContext()
            if (tenantData?.id && tenantData?.backendUrl) {
                console.log('[AuthService] Trying token refresh endpoint...')
                const refreshUrl = `${tenantData.backendUrl}/api/public/tenants/auth/refresh`
                const response = await fetch(refreshUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        tenantId: tenantData.id,
                        redmineUserId: session.user.id,
                    }),
                })

                if (response.ok) {
                    const data = await response.json()
                    if (data.accessToken) {
                        const updatedSession: AuthSession = { ...session, jwtToken: data.accessToken }
                        this.storeSession(updatedSession)
                        console.log('[AuthService] JWT token refreshed via refresh endpoint')
                        return updatedSession
                    }
                }
                console.warn('[AuthService] Refresh endpoint failed, falling back to re-registration...')
            }
        } catch (error) {
            console.warn('[AuthService] Refresh endpoint error, falling back to re-registration:', error)
        }

        // Fall back to full re-registration using the stored API key
        const apiKey = session.apiKey || session.user.api_key
        if (!apiKey) {
            console.warn('[AuthService] No API key available for JWT re-registration. User needs to re-login.')
            return null
        }

        try {
            const jwtToken = await this.registerUserWithBackend(session.user, apiKey)
            if (jwtToken) {
                const updatedSession: AuthSession = { ...session, jwtToken }
                this.storeSession(updatedSession)
                console.log('[AuthService] JWT token obtained via re-registration')
                return updatedSession
            } else {
                console.warn('[AuthService] JWT re-registration returned no token')
                return null
            }
        } catch (error) {
            console.error('[AuthService] JWT re-registration failed:', error)
            return null
        }
    }

    /**
     * Get tenant context from cookie
     */
    private getTenantContext(): any {
        if (typeof window === 'undefined') return null

        const cookies = document.cookie.split(';')
        const tenantCookie = cookies.find(c => c.trim().startsWith('tenant-context='))

        if (tenantCookie) {
            try {
                const cookieValue = tenantCookie.split('=').slice(1).join('=').trim()
                return JSON.parse(decodeURIComponent(cookieValue))
            } catch (error) {
                return null
            }
        }

        return null
    }

    /**
     * Get authentication headers for API calls
     */
    getAuthHeaders(): { [key: string]: string } | null {
        const session = this.getSession()
        if (!session || !this.isSessionValid(session)) {
            return null
        }

        if (session.credentials) {
            return {
                'Authorization': `Basic ${session.credentials.basicAuth}`,
                'Content-Type': 'application/json'
            }
        } else if (session.apiKey) {
            return {
                'X-Redmine-API-Key': session.apiKey,
                'Content-Type': 'application/json'
            }
        }

        return null
    }

    /**
     * Get Redmine base URL from tenant configuration
     */
    getRedmineBaseUrl(): string {
        // Try to get from tenant context first
        if (typeof window !== 'undefined') {
            const cookies = document.cookie.split(';')
            const tenantCookie = cookies.find(c => c.trim().startsWith('tenant-context='))

            if (tenantCookie) {
                try {
                    const cookieValue = tenantCookie.split('=').slice(1).join('=').trim()
                    const tenantData = JSON.parse(decodeURIComponent(cookieValue))
                    if (tenantData.redmineBaseUrl) {
                        return tenantData.redmineBaseUrl
                    }
                } catch (error) {
                    console.error('Failed to parse tenant cookie:', error)
                }
            }
        }

        // Fallback for development/testing
        return 'https://oxigen.oxinus.holdings'
    }

    /**
     * Refresh session if close to expiry
     * NOTE: Since we don't persist credentials, we can only refresh API key sessions.
     * Password-based sessions will require re-login on expiry.
     */
    async refreshSession(): Promise<AuthSession | null> {
        const currentSession = this.getSession()
        if (!currentSession) return null

        try {
            // Fetch latest user data to ensure we have all fields (especially email)
            const headers: { [key: string]: string } = {
                'Content-Type': 'application/json'
            }

            if (currentSession.apiKey) {
                headers['X-Redmine-API-Key'] = currentSession.apiKey
            } else if (currentSession.credentials?.basicAuth) {
                headers['Authorization'] = `Basic ${currentSession.credentials.basicAuth}`
            } else {
                // No valid auth method, logout
                this.logout()
                return null
            }

            const response = await fetch('/api/redmine?path=/users/current.json', {
                headers
            })

            if (!response.ok) {
                this.logout()
                return null
            }

            const responseData = await response.json()
            const updatedUser: RedmineUser = responseData.user

            // Update session with fresh user data
            const newExpiresAt = Date.now() + (TOKEN_EXPIRY_HOURS * 60 * 60 * 1000)
            const refreshedSession: AuthSession = {
                ...currentSession,
                user: updatedUser,
                expiresAt: newExpiresAt
            }

            this.storeSession(refreshedSession)
            return refreshedSession
        } catch (error) {
            console.error('Failed to refresh session:', error)
            // Don't logout on refresh failure, just return current session
            return currentSession
        }
    }

    /**
     * Extend current session (useful for keeping active users logged in)
     */
    extendSession(): boolean {
        try {
            const session = this.getSession()
            if (!session) return false

            // Extend session by TOKEN_EXPIRY_HOURS from now
            const newExpiresAt = Date.now() + (TOKEN_EXPIRY_HOURS * 60 * 60 * 1000)
            const extendedSession = {
                ...session,
                expiresAt: newExpiresAt
            }

            this.storeSession(extendedSession)
            return true
        } catch (error) {
            return false
        }
    }

    /**
     * Get authenticated Redmine client
     */
    getRedmineClient(): RedmineClient | null {
        const session = this.getSession()
        if (!session) return null

        return new RedmineClient({
            baseUrl: this.getRedmineBaseUrl(),
            apiKey: session.apiKey,
        })
    }

    /**
     * Test connection to Redmine server (console logging only)
     */
    async testConnection(): Promise<{ success: boolean; error?: string; details?: any }> {
        try {

            // Use the dedicated connection test endpoint that doesn't require auth
            const response = await axios.get('/api/test-connection', {
                timeout: 10000,
            })

            const result = response.data

            if (result.success) {
                if (result.details) {
                }
            } else {
            }

            return result
        } catch (error) {

            let errorMessage = 'Unknown error'
            let details = null

            if (axios.isAxiosError(error)) {
                if (error.response) {
                    errorMessage = `Server error: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`
                    details = error.response.data
                } else if (error.request) {
                    errorMessage = 'No response from server - check if Redmine is running'
                } else {
                    errorMessage = error.message
                }
            }

            return { success: false, error: errorMessage, details }
        }
    }

    /**
     * Initialize auth service and test connection
     */
    async initialize(): Promise<void> {
        await this.testConnection()
    }

    /**
     * Generate a session token (simple implementation)
     */
    private generateSessionToken(userId: number): string {
        const timestamp = Date.now()
        const random = Math.random().toString(36).substring(2)
        return `oxigen_${userId}_${timestamp}_${random}`
    }

    /**
     * Store session in localStorage
     * NOTE: Credentials (password/basicAuth) are NEVER persisted to localStorage for security.
     * Only user info, token, expiry, and API key (if using API key auth) are stored.
     */
    private storeSession(session: AuthSession): void {
        if (typeof window !== 'undefined') {
            // Create a copy without sensitive credentials
            const sessionToStore: AuthSession = {
                ...session,
                credentials: undefined // Never persist password/basicAuth
            }

            try {
                const encrypted = encryptSessionObjectSync(sessionToStore)
                if (encrypted) {
                    localStorage.setItem(SESSION_KEY, encrypted)
                } else {
                    localStorage.setItem(SESSION_KEY, JSON.stringify(sessionToStore))
                }
            } catch (err) {
                console.error('[AuthService] Failed to encrypt session', err)
                localStorage.setItem(SESSION_KEY, JSON.stringify(sessionToStore))
            }

            // Store JWT token as HTTP-only cookie for API routes
            if (session.jwtToken) {
                // Set cookie with 24h expiry (matching JWT token expiry)
                const expiryDate = new Date(session.expiresAt)
                document.cookie = `jwt-token=${session.jwtToken}; path=/; expires=${expiryDate.toUTCString()}; SameSite=Lax`
                console.log('[AuthService] JWT token stored in cookie')
            }
        }
    }
}

// Export singleton instance
export const authService = new AuthService()